import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

async function addLumiraToCart({
  variantId,
  quantity = 1,
  sourceElement,
  productId = ''
}) {
  if (!variantId) {
    throw new Error(
      'No se ha encontrado la variante.'
    );
  }

  /*
   * Recogemos TODAS las secciones de carrito que Horizon
   * necesita volver a renderizar.
   *
   * En tu drawer:
   * data-section-id="cart-drawer-section"
   */
  const sectionIds = [];

  document
    .querySelectorAll(
      'cart-items-component[data-section-id]'
    )
    .forEach((component) => {
      const sectionId =
        component.dataset.sectionId;

      if (
        sectionId &&
        !sectionIds.includes(sectionId)
      ) {
        sectionIds.push(sectionId);
      }
    });

  /*
   * Creamos EXACTAMENTE la promesa que espera Horizon.
   */
  const deferredEventPromise =
    CartLinesUpdateEvent.createPromise();

  /*
   * Disparamos el evento ANTES del fetch.
   *
   * cart-drawer-component y cart-items-component
   * empiezan a escuchar esta operación.
   */
  sourceElement.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',

      context: 'cart',

      lines: [
        {
          merchandiseId:
            String(variantId),

          quantity:
            Number(quantity)
        }
      ],

      promise:
        deferredEventPromise.promise
    })
  );

  try {
    /*
     * IMPORTANTE:
     *
     * No hacemos simplemente cart/add.js.
     *
     * Pedimos también las secciones que Horizon
     * necesita para actualizar visualmente el drawer.
     */
    const payload = {
      items: [
        {
          id: Number(variantId),
          quantity: Number(quantity)
        }
      ],

      sections:
        sectionIds.join(','),

      sections_url:
        window.location.pathname
    };

    const response = await fetch(
      `${getLumiraRoot()}cart/add.js`,
      {
        method: 'POST',

        credentials: 'same-origin',

        headers: {
          'Content-Type':
            'application/json',

          Accept:
            'application/json'
        },

        body:
          JSON.stringify(payload)
      }
    );

    const responseData =
      await response.json();

    if (
      !response.ok ||
      responseData.status
    ) {
      const error = new Error(
        responseData.description ||
        responseData.message ||
        'No se pudo añadir el producto.'
      );

      deferredEventPromise.reject(
        error
      );

      throw error;
    }

    /*
     * Obtenemos carrito actualizado.
     */
    const cart =
      await getLumiraCart();

    /*
     * AQUÍ ESTÁ LA CLAVE.
     *
     * Resolvemos el CartLinesUpdateEvent con:
     *
     * - sections
     * - items
     * - itemCount
     * - didError
     *
     * Entonces cart-items-component de Horizon
     * ejecuta morphSection() automáticamente.
     */
    deferredEventPromise.resolve({
      cart:
        CartLinesUpdateEvent
          .createCartFromAjaxResponse(
            cart
          ),

      detail: {
        items:
          cart.items,

        source:
          'lumira-cart-upsell',

        sourceId:
          'lumira-cart-upsell',

        itemCount:
          Number(quantity),

        productId:
          String(productId || ''),

        sections:
          responseData.sections,

        didError:
          false
      }
    });

    return cart;

  } catch (error) {
    try {
      deferredEventPromise.reject(
        error
      );
    } catch (_) {
      // Ya resuelta/rechazada.
    }

    throw error;
  }
}



// Un complemento, una variante concreta y una sola pulsación para añadir.
const SLOT = '#cart-drawer [data-cart-recommendations-slot]';
const cache = new Map();
let generation = 0;
let pendingFrame = null;
let observedDrawer = null;
let observer = null;
let adding = false;
let savedSelection = null;
let savedCurrency = 'EUR';
let savedSignature = '';
const ACCEPTED_KEY = 'lumira_cart_upsell_accepted_v3';
let accepted = false;
try { accepted = sessionStorage.getItem(ACCEPTED_KEY) === '1'; } catch { /* Memoria de esta página. */ }

function setAccepted(value) {
  accepted = value;
  try {
    if (value) sessionStorage.setItem(ACCEPTED_KEY, '1');
    else sessionStorage.removeItem(ACCEPTED_KEY);
  } catch { /* Memoria de esta página. */ }
}
function clearRecommendation(slot) {
  if (!slot) return;
  if (slot.firstChild) slot.replaceChildren();
  if (!slot.hidden) slot.hidden = true;
}
function resetRecommendationSession() {
  setAccepted(false);
  savedSelection = null;
  savedSignature = '';
  ++generation;
}
function restoreRecommendation() {
  const drawer = document.querySelector('#cart-drawer');
  if (drawer?.querySelector('.cart-drawer--empty')) {
    if (accepted || savedSelection || savedSignature) resetRecommendationSession();
    return;
  }
  const slot = document.querySelector(SLOT);
  if (!slot) return;
  if (accepted) { clearRecommendation(slot); return; }
  if (!savedSelection) return;
  const button = slot.querySelector('[data-lumira-cart-add]');
  if (button?.dataset.variantId === String(savedSelection.variant.id)) {
    if (slot.hidden) slot.hidden = false;
    return;
  }
  // Restaurar en la misma microtarea del morph, ANTES del siguiente pintado.
  renderRecommendation(slot, savedSelection, savedCurrency);
  if (adding) {
    const restoredButton = slot.querySelector('[data-lumira-cart-add]');
    if (restoredButton) { restoredButton.disabled = true; restoredButton.textContent = 'Añadiendo…'; }
  }
}

function getLumiraRoot() { return window.Shopify?.routes?.root || '/'; }
function escapeHtml(value = '') {
  return String(value).replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
}
function normalize(value) {
  return String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
}
function imageSource(image) { return typeof image === 'string' ? image : image?.src || image?.url || ''; }
function imageKey(image) { return imageSource(image).split('?')[0].replace(/^https?:/, ''); }
function money(cents, currency) {
  return new Intl.NumberFormat('es-ES', {style:'currency', currency}).format(Number(cents) / 100);
}
async function requestJson(url) {
  const response = await fetch(url, {credentials:'same-origin', cache:'no-store', headers:{Accept:'application/json'}});
  if (!response.ok) throw new Error(`No se pudo cargar el producto (${response.status}).`);
  return response.json();
}
function cachedJson(url) {
  const existing = cache.get(url);
  if (existing && Date.now() - existing.time < 60000) return existing.promise;
  const promise = requestJson(url).catch(error => { cache.delete(url); throw error; });
  cache.set(url, {time:Date.now(), promise});
  return promise;
}
async function getLumiraCart() { return requestJson(`${getLumiraRoot()}cart.js`); }

function isAccessory(product) {
  const category = normalize(`${product.type || product.product_type || ''} ${product.title || ''}`);
  const tags = normalize(Array.isArray(product.tags) ? product.tags.join(' ') : product.tags);
  // Nunca recomendar prendas aunque su título mencione un complemento.
  if (/\b(vestido|vestidos|dress|dresses|camisa|blusa|pantalon|falda|abrigo|chaqueta|jersey|zapatos|sandalias|botas)\b/.test(category)) return false;
  return /\b(pendientes|pendiente|earrings|earring|pulsera|pulseras|bracelet|bracelets|collar|collares|necklace|necklaces|bolso|bolsos|bag|bags|cinturon|cinturones|belt|belts|panuelo|panuelos|scarf|scarves|broche|broches|accesorios|accesorio|accessories|complementos|complemento|joyeria|bisuteria|jewelry|jewellery)\b/.test(category) ||
    /(^|[ ,])(lumira-upsell|lumira-complemento)([ ,]|$)/.test(tags);
}

function chooseVariant(product, maxPrice) {
  const variants = product.variants || [];
  const options = product.options || [];
  // Varias tallas o cualquier otra selección no visual: no ofrecer este producto.
  for (let i = 0; i < options.length; i++) {
    const name = normalize(typeof options[i] === 'string' ? options[i] : options[i].name);
    const values = new Set(variants.map(v => v.options?.[i]).filter(Boolean));
    if (values.size > 1 && !/color|colour|colore|couleur|farbe|acabado|finish|material/.test(name)) return null;
  }
  const available = variants.filter(v => v.available && Number(v.price) > 0 && Number(v.price) <= maxPrice && !v.requires_selling_plan);
  if (!available.length || product.requires_selling_plan) return null;
  const cover = imageKey(product.featured_image || product.images?.[0]);
  // Preferir la variante de la portada; en otro color se muestra SU imagen.
  const exact = available.find(v => imageKey(v.featured_image) && imageKey(v.featured_image) === cover);
  if (exact) return exact;
  const pictured = available.find(v => imageSource(v.featured_image));
  if (pictured) return pictured;
  // Sin asociación imagen/variante no se adivina el color de una foto.
  if (variants.length === 1 && imageSource(product.featured_image || product.images?.[0])) return available[0];
  return null;
}

async function candidates(productId, intent) {
  const url = `${getLumiraRoot()}recommendations/products.json?product_id=${encodeURIComponent(productId)}&limit=10&intent=${intent}`;
  const data = await cachedJson(url);
  return data.products || [];
}

async function selectRecommendation(cart, maxPrice) {
  const included = new Set(cart.items.map(item => String(item.product_id)));
  const base = [...cart.items].sort((a,b) => Number(isAccessory(a)) - Number(isAccessory(b)));
  const sources = [...new Set(base.map(item => item.product_id))].slice(0, 3);
  const checked = new Set();
  // Primero los complementos configurados; después relacionados filtrados.
  for (const intent of ['complementary', 'related']) {
    for (const id of sources) {
      let products;
      try { products = await candidates(id, intent); } catch { continue; }
      for (const candidate of products) {
        if (included.has(String(candidate.id)) || checked.has(String(candidate.id))) continue;
        checked.add(String(candidate.id));
        if (!isAccessory(candidate) || !candidate.handle) continue;
        try {
          const product = await cachedJson(`${getLumiraRoot()}products/${encodeURIComponent(candidate.handle)}.js`);
          if (!isAccessory(product)) continue;
          const variant = chooseVariant(product, maxPrice);
          if (variant) return {product, variant};
        } catch { /* Probar el siguiente complemento. */ }
      }
    }
  }
  // Compatibilidad con los complementos configurados en la sección anterior.
  for (const id of sources) {
    try {
      const url = `${getLumiraRoot()}recommendations/products?section_id=cart-recommendations&product_id=${encodeURIComponent(id)}&limit=10&intent=complementary`;
      const response = await fetch(url, {credentials:'same-origin',headers:{Accept:'text/html'}});
      if (!response.ok) continue;
      const doc = new DOMParser().parseFromString(await response.text(),'text/html');
      const root = doc.querySelector('[data-lumira-cart-upsell], [data-cart-recommendations-content]');
      if (!root) continue;
      const handles = new Set();
      root.querySelectorAll('[data-product-handle], a[href*="/products/"]').forEach(element => {
        const handle = element.dataset.productHandle || element.getAttribute('href')?.match(/\/products\/([^?#/]+)/)?.[1];
        if (handle) handles.add(decodeURIComponent(handle));
      });
      for (const handle of [...handles].slice(0,10)) {
        try {
          const product = await cachedJson(`${getLumiraRoot()}products/${encodeURIComponent(handle)}.js`);
          if (included.has(String(product.id)) || !isAccessory(product)) continue;
          const variant = chooseVariant(product,maxPrice);
          if (variant) return {product,variant};
        } catch { /* Probar el siguiente. */ }
      }
    } catch { /* Una sección ausente no interrumpe el carrito. */ }
  }
  return null;
}

function renderRecommendation(slot, selection, currency) {
  if (!selection) { slot.replaceChildren(); slot.hidden = true; return; }
  const {product, variant} = selection;
  const image = imageSource(variant.featured_image) || imageSource(product.featured_image || product.images?.[0]);
  const compare = Number(variant.compare_at_price || 0);
  const discounted = compare > Number(variant.price);
  const percent = discounted ? Math.floor((compare - Number(variant.price)) * 100 / compare) : 0;
  const title = variant.title && variant.title !== 'Default Title' ? variant.title : '';
  const url = `${getLumiraRoot()}products/${encodeURIComponent(product.handle)}?variant=${encodeURIComponent(variant.id)}`;
  slot.innerHTML = `
    <section class="lumira-cart-recommendations" data-lumira-cart-upsell aria-label="Complemento recomendado">
      <div class="lumira-cart-recommendations__header"><h3 class="lumira-cart-recommendations__title">${discounted ? 'Añádelo con descuento' : 'Completa tu pedido'}</h3></div>
      <div class="lumira-cart-recommendations__list">
        <article class="lumira-cart-recommendation" data-lumira-upsell-product data-product-id="${escapeHtml(product.id)}">
          <a class="lumira-cart-recommendation__media" href="${escapeHtml(url)}" tabindex="-1" aria-hidden="true">
            <img class="lumira-cart-recommendation__image" src="${escapeHtml(image)}" alt="" width="80" height="86" loading="eager">
            ${percent ? `<span class="lumira-cart-recommendation__discount">-${percent}%</span>` : ''}
          </a>
          <div class="lumira-cart-recommendation__content">
            <a class="lumira-cart-recommendation__name" href="${escapeHtml(url)}">${escapeHtml(product.title)}</a>
            ${title ? `<span class="lumira-cart-recommendation__variant">${escapeHtml(title)}</span>` : ''}
            <div class="lumira-cart-recommendation__prices">
              <strong class="lumira-cart-recommendation__price">${escapeHtml(money(variant.price,currency))}</strong>
              ${discounted ? `<s class="lumira-cart-recommendation__compare-price">${escapeHtml(money(compare,currency))}</s>` : ''}
            </div>
          </div>
          <button class="lumira-cart-recommendation__button" type="button" data-lumira-cart-add data-variant-id="${escapeHtml(variant.id)}" aria-label="Añadir ${escapeHtml(product.title)}${title ? ', '+escapeHtml(title) : ''}">Añadir</button>
        </article>
      </div>
      <p class="lumira-cart-recommendation__error" role="status" hidden></p>
    </section>`;
  slot.hidden = false;
}

async function refresh() {
  restoreRecommendation();
  const token = ++generation;
  const slot = document.querySelector(SLOT);
  if (!slot || accepted || adding) return;
  try {
    const cart = await getLumiraCart();
    if (token !== generation || !slot.isConnected || accepted || adding) return;
    if (!cart.items.length) {
      resetRecommendationSession();
      clearRecommendation(slot);
      return;
    }
    const currency = cart.currency || window.Shopify?.currency?.active || 'EUR';
    const maxPrice = Number(slot.dataset.maxPrice) || 2500;
    // Cantidades y total no cambian la identidad de la recomendación.
    const ids = [...new Set(cart.items.map(item => String(item.product_id)))].sort();
    const signature = `${currency}:${maxPrice}:${ids.join(',')}`;
    if (signature === savedSignature) {
      restoreRecommendation();
      return;
    }
    const selection = await selectRecommendation(cart, maxPrice);
    if (token !== generation || !slot.isConnected || adding || accepted) return;
    savedSelection = selection;
    savedCurrency = currency;
    savedSignature = signature;
    if (selection) restoreRecommendation();
    else clearRecommendation(slot);
  } catch (error) {
    // Un fallo de red no borra una tarjeta que ya se había mostrado.
    console.warn('[Lumira complemento]',error);
  }
}
function scheduleRefresh() {
  restoreRecommendation();
  if (pendingFrame !== null) cancelAnimationFrame(pendingFrame);
  pendingFrame = requestAnimationFrame(() => { pendingFrame = null; refresh(); });
}
function initialize() {
  const drawer = document.querySelector('#cart-drawer');
  if (drawer !== observedDrawer) {
    observer?.disconnect();
    observedDrawer = drawer;
    if (drawer) {
      observer = new MutationObserver(mutations => {
        restoreRecommendation();
        const relevant = mutations.some(m => {
          if (m.type === 'attributes') return m.target.matches('[data-cart-recommendations-slot]');
          return [...m.addedNodes].some(node => node instanceof Element && (
            node.matches('[data-cart-recommendations-slot]') || node.querySelector('[data-cart-recommendations-slot]')
          ));
        });
        if (relevant) scheduleRefresh();
      });
      observer.observe(drawer,{subtree:true,childList:true,attributes:true,attributeFilter:['data-cart-product-ids','data-product-id']});
    }
  }
  scheduleRefresh();
}

document.addEventListener('click', async event => {
  if (!(event.target instanceof Element)) return;
  const button = event.target.closest('#cart-drawer [data-lumira-cart-add]');
  if (!button || button.disabled || adding) return;
  event.preventDefault();
  adding = true;
  button.disabled = true;
  button.textContent = 'Añadiendo…';
  const section = button.closest('[data-lumira-cart-upsell]');
  const errorBox = section?.querySelector('.lumira-cart-recommendation__error');
  if(errorBox) errorBox.hidden = true;
  try {
    await addLumiraToCart({
      variantId:button.dataset.variantId, quantity:1, sourceElement:button,
      productId:button.closest('[data-product-id]')?.dataset.productId || ''
    });
    setAccepted(true);
    savedSelection = null;
    savedSignature = '';
    ++generation;
    clearRecommendation(document.querySelector(SLOT));
  } catch(error) {
    const activeButton = document.querySelector(`${SLOT} [data-lumira-cart-add]`) || button;
    if (activeButton.isConnected) {
      activeButton.disabled = false;
      activeButton.textContent = 'Reintentar';
      const activeError = activeButton.closest('[data-lumira-cart-upsell]')?.querySelector('.lumira-cart-recommendation__error');
      if(activeError) {activeError.textContent = error.message || 'No se pudo añadir. Inténtalo de nuevo.'; activeError.hidden = false;}
    }
  } finally {
    adding = false;
    cache.clear();
    restoreRecommendation();
  }
});
document.addEventListener(StandardEvents.cartLinesUpdate, event => {
  event.promise?.then(() => scheduleRefresh()).catch(() => {});
});
document.addEventListener('shopify:section:load', initialize);
window.addEventListener('pageshow', initialize);
if(document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize,{once:true});
else initialize();

// Evitar el marco de enfoque automático; conservar indicación para teclado.
let keyboardNavigation = false;
function updateFocusMode() {
  document.querySelector('#cart-drawer')?.toggleAttribute('data-lumira-keyboard-focus', keyboardNavigation);
}
document.addEventListener('keydown', event => {
  if (event.key === 'Tab') { keyboardNavigation = true; updateFocusMode(); }
}, true);
document.addEventListener('pointerdown', () => {
  keyboardNavigation = false;
  updateFocusMode();
}, true);
document.addEventListener('shopify:section:load', updateFocusMode);
